import { useDialogs } from "../ui/DialogProvider";
import React from "react";
import {
  ADMIN_POLL_INTERVAL,
  ADMIN_PROGRESS_PREFIX,
  API_BASE,
  fetchJson,
} from "../utils";
import {
  buildAdminEndpoint,
  buildAdminRequestOptions,
  interpretRunResult,
  resolveAdminResult,
} from "../utils/adminOps";
import { validateJob } from "../api/jobs";
import { describeApiError } from "../api/request";
import {
  readStoredValue,
  writeStoredValue,
  removeStoredValue,
} from "../utils/storage";
export default function useAdminOperation(op, idle, runOp, { onReset } = {}) {
  const dialogs = useDialogs();
  const [running, setRunning] = React.useState(false);
  const [status, setStatus] = React.useState(null);
  const state = React.useRef({
    endpoint: null,
    active: true,
    busy: false,
    failures: 0,
  });
  const pollRef = React.useRef(null);
  const idleRef = React.useRef(idle);
  idleRef.current = idle;
  const storageKey = `${ADMIN_PROGRESS_PREFIX}${op.key}`;
  const finalize = React.useCallback(
    (reset = false) => {
      clearTimeout(state.current.timer);
      state.current.endpoint = null;
      setRunning(false);
      removeStoredValue(storageKey);
      if (reset) onReset?.();
    },
    [storageKey, onReset],
  );
  const schedule = React.useCallback(() => {
    const current = state.current;
    clearTimeout(current.timer);
    if (
      !current.active ||
      !current.endpoint ||
      op.poll === false ||
      idleRef.current ||
      document.hidden
    )
      return;
    current.timer = setTimeout(
      () => pollRef.current?.(),
      Math.min(30000, ADMIN_POLL_INTERVAL * 2 ** current.failures),
    );
  }, [op.poll]);
  const poll = React.useCallback(async () => {
    const current = state.current;
    if (
      !current.endpoint ||
      current.busy ||
      idleRef.current ||
      document.hidden ||
      !current.active
    )
      return;
    current.busy = true;
    current.controller = new AbortController();
    try {
      const data = await fetchJson(`${API_BASE}/admin/${current.endpoint}`, {
        signal: current.controller.signal,
      });
      if (!current.active) return;
      if (op.key.includes("fetch-bpm"))
        validateJob(data, op.key === "track-fetch-bpm" ? "track" : "bpm");
      current.failures = 0;
      const result = resolveAdminResult(data, op);
      setStatus(result.status);
      if (result.kind === "running") schedule();
      else finalize(result.resetInputs);
    } catch (error) {
      if (!current.active || error.name === "AbortError") return;
      setStatus({
        error: describeApiError(error),
      });
      if (!error.status || error.status < 400 || error.status >= 500) {
        current.failures += 1;
        schedule();
      } else finalize();
    } finally {
      current.busy = false;
    }
  }, [op, schedule, finalize]);
  pollRef.current = poll;
  React.useEffect(() => {
    // Each effect lifetime owns its requests. An aborted previous setup must
    // not regain authority when React replays effects or the operation changes.
    const current = {
      endpoint: null,
      active: true,
      busy: false,
      failures: 0,
    };
    state.current = current;
    const saved = readStoredValue(storageKey);
    if (saved && op.poll !== false) {
      current.endpoint = saved;
      setRunning(true);
      pollRef.current();
    }
    const resume = () => {
      if (!document.hidden) pollRef.current();
    };
    document.addEventListener("visibilitychange", resume);
    return () => {
      current.active = false;
      current.controller?.abort();
      clearTimeout(current.timer);
      document.removeEventListener("visibilitychange", resume);
    };
  }, [storageKey, op.poll]);
  React.useEffect(() => {
    if (idle) clearTimeout(state.current.timer);
    else schedule();
  }, [idle, schedule]);
  const runOperation = async (context) => {
    const current = state.current;
    if (current.endpoint || current.busy) return;
    if (op.needsUser && op.userRequired && !context?.userIds?.length) return;
    const { endpoint, missingId } = buildAdminEndpoint(op, context);
    if (
      !endpoint ||
      missingId ||
      !(await dialogs.confirm("Run this operation?"))
    )
      return;
    if (!current.active || current.endpoint || current.busy) return;
    current.endpoint = endpoint;
    current.busy = true;
    current.failures = 0;
    current.controller = new AbortController();
    setRunning(true);
    setStatus({
      message: "Starting…",
    });
    writeStoredValue(storageKey, endpoint);
    try {
      const response = await runOp(
        endpoint,
        current.controller.signal,
        op.method,
        buildAdminRequestOptions(op, context),
      );
      if (!current.active) return;
      if (op.key.includes("fetch-bpm"))
        validateJob(
          response.data,
          op.key === "track-fetch-bpm" ? "track" : "bpm",
        );
      const result = interpretRunResult(response.data, response.status, op);
      setStatus(result.status);
      if (result.shouldPoll && op.poll !== false) schedule();
      else finalize(result.resetInputs);
    } catch (error) {
      if (current.active && error.name !== "AbortError") {
        setStatus({
          error: describeApiError(error),
        });
        // Unknown POST outcomes are checked with GET; never automatically POST again.
        if (
          (!error.status || error.status < 400 || error.status >= 500) &&
          op.poll !== false
        )
          schedule();
        else finalize();
      }
    } finally {
      current.busy = false;
    }
  };
  const cancelOperation = async () => {
    const current = state.current;
    const endpoint = current.endpoint;
    if (
      !current.endpoint ||
      current.busy ||
      !op.cancellable ||
      status?.status === "stopping"
    )
      return;
    if (
      !(await dialogs.confirm(
        "Stop one running job? Other queued work may continue.",
      ))
    )
      return;
    if (!current.active || current.endpoint !== endpoint || current.busy)
      return;
    current.busy = true;
    clearTimeout(current.timer);
    try {
      const data = await fetchJson(`${API_BASE}/admin/${current.endpoint}`, {
        method: "DELETE",
        signal: current.controller?.signal,
      });
      if (!current.active) return;
      if (op.key.includes("fetch-bpm"))
        validateJob(data, op.key === "track-fetch-bpm" ? "track" : "bpm");
      setStatus({
        ...data,
        message: "Stopping job…",
      });
      schedule();
    } catch (error) {
      if (current.active && error.name !== "AbortError") {
        setStatus({
          error: describeApiError(error),
        });
        if (error.status === 401 || error.status === 403) finalize();
        else schedule();
      }
    } finally {
      current.busy = false;
    }
  };
  const known = running && status?.progress != null && status?.import_total > 0;
  const progress = {
    known,
    percent: known ? (status.progress / status.import_total) * 100 : 0,
    current: known ? status.progress : null,
    total: known ? status.import_total : null,
  };
  return {
    running,
    status,
    progress,
    runOperation,
    cancelOperation,
  };
}
